import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../utils/currency';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loadError, setLoadError] = useState('');
  
  // Sorting
  const { items: sortedInvoices, requestSort, getSortIndicator } = useSortableTable(invoices, { key: 'invoice_ref', direction: 'desc' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    invoiceRef: true,
    customer: true,
    site: true,
    invoiceDate: true,
    dueDate: true,
    total: true,
    paid: true,
    balance: true,
    status: true,
  });
  
  // CSV Export state
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportEmail, setExportEmail] = useState('');
  const [exportStatusFilter, setExportStatusFilter] = useState('all');
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');

  useEffect(() => {
    fetchInvoices();
    loadColumnPreferences();
  }, []);

  const loadColumnPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/invoices');
      const data = await response.json();
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
  };

  const saveColumnPreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/invoices', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  };

  const toggleColumn = (column) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [column]: !visibleColumns[column]
    };
    setVisibleColumns(newVisibleColumns);
    saveColumnPreferences(newVisibleColumns);
  };

  const allColumns = [
    { key: 'invoiceRef', label: 'Invoice Ref' },
    { key: 'customer', label: 'Customer' },
    { key: 'site', label: 'Site' },
    { key: 'invoiceDate', label: 'Invoice Date' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'total', label: 'Total' },
    { key: 'paid', label: 'Paid' },
    { key: 'balance', label: 'Balance' },
    { key: 'status', label: 'Status' },
  ];

  const fetchInvoices = async () => {
    try {
      setLoadError('');
      const response = await apiFetch('/api/invoices');
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load invoices');
      }
      if (!Array.isArray(data)) {
        throw new Error(data.error || 'Invoice API returned an unexpected response');
      }
      setInvoices(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setLoadError(error.message || 'Failed to load invoices');
      setLoading(false);
    }
  };

  const filteredInvoices = sortedInvoices.filter(invoice =>
    invoice.invoice_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      partial: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      partial: 'Partial',
      paid: 'Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const toggleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
    }
  };

  const getExportInvoices = () => {
    let invoicesToExport = selectedInvoices.length > 0
      ? invoices.filter(inv => selectedInvoices.includes(inv.id))
      : filteredInvoices;

    // Apply status filter
    if (exportStatusFilter !== 'all') {
      invoicesToExport = invoicesToExport.filter(inv => inv.status === exportStatusFilter);
    }

    // Apply date range filter
    if (exportDateFrom) {
      invoicesToExport = invoicesToExport.filter(inv => 
        new Date(inv.invoice_date) >= new Date(exportDateFrom)
      );
    }
    if (exportDateTo) {
      invoicesToExport = invoicesToExport.filter(inv => 
        new Date(inv.invoice_date) <= new Date(exportDateTo)
      );
    }

    return invoicesToExport;
  };

  const generateCSV = (invoicesToExport) => {
    // CSV headers
    const headers = [
      'Invoice Ref',
      'Customer',
      'Site',
      'Invoice Date',
      'Due Date',
      'Subtotal',
      'VAT',
      'Total',
      'Paid',
      'Balance',
      'Status'
    ];

    // CSV rows
    const rows = invoicesToExport.map(inv => [
      inv.invoice_ref || '',
      inv.customer_name || '',
      inv.site_name || 'Head Office',
      inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB') : '',
      inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-GB') : '',
      (parseFloat(inv.subtotal || 0)).toFixed(2),
      (parseFloat(inv.vat_amount || 0)).toFixed(2),
      (parseFloat(inv.total || 0)).toFixed(2),
      (parseFloat(inv.paid_amount || 0)).toFixed(2),
      (parseFloat(inv.balance || 0)).toFixed(2),
      inv.status || ''
    ]);

    // Build CSV content
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
  };

  const handleDownloadCSV = () => {
    const invoicesToExport = getExportInvoices();
    
    if (invoicesToExport.length === 0) {
      alert('No invoices match the export criteria');
      return;
    }

    const csvContent = generateCSV(invoicesToExport);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().split('T')[0];
    
    link.setAttribute('href', url);
    link.setAttribute('download', `invoices-${today}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setShowExportModal(false);
    setSelectedInvoices([]);
  };

  const handleEmailCSV = async () => {
    if (!exportEmail) {
      alert('Please enter an email address');
      return;
    }

    const invoicesToExport = getExportInvoices();
    
    if (invoicesToExport.length === 0) {
      alert('No invoices match the export criteria');
      return;
    }

    const csvContent = generateCSV(invoicesToExport);
    
    try {
      const response = await apiFetch('/api/invoices/email-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: exportEmail,
          csvContent,
          count: invoicesToExport.length,
          dateFrom: exportDateFrom,
          dateTo: exportDateTo
        })
      });

      if (response.ok) {
        alert(`CSV emailed to ${exportEmail} successfully!`);
        setShowExportModal(false);
        setSelectedInvoices([]);
        setExportEmail('');
      } else {
        alert('Failed to send email');
      }
    } catch (error) {
      console.error('Email error:', error);
      alert('Error sending email');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Invoices"
        subtitle={`${invoices.length} total`}
        backButton
        onBack={() => navigate('/finance')}
        backTitle="Back to Finance"
        sticky
        actionButton={
          <div className="flex items-center gap-2">
            {selectedInvoices.length > 0 && (
              <span className="text-sm text-white bg-blue-600 px-3 py-2 rounded">
                {selectedInvoices.length} selected
              </span>
            )}
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
              title="Export to CSV"
            >
              📊 Export CSV
            </button>
            <button
              onClick={() => navigate('/finance/invoices/new')}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
            >
              ➕ New Invoice
            </button>
          </div>
        }
      />

      <div className="px-4 py-3">
        {/* Search + Column Selector */}
        <div className="mb-3 flex items-center gap-2">
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2"
            >
              ⚙️ Columns
            </button>
            {showColumnSelector && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowColumnSelector(false)}
                />
                <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-2">
                  <div className="px-3 py-2 border-b border-gray-200">
                    <div className="text-xs font-semibold text-gray-600">Show/Hide Columns</div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {allColumns.map(col => (
                      <label
                        key={col.key}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded border border-gray-200 ">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative"><table className="w-full text-sm">
            <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
              <tr>
                <th className="px-3 py-1.5 text-xs font-semibold text-gray-600">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                    onChange={toggleSelectAll}
                    className="cursor-pointer"
                  />
                </th>
                {visibleColumns.invoiceRef && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('invoice_ref')}>Invoice Ref{getSortIndicator('invoice_ref')}</th>}
                {visibleColumns.customer && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('customer_name')}>Customer{getSortIndicator('customer_name')}</th>}
                {visibleColumns.site && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_name')}>Site{getSortIndicator('site_name')}</th>}
                {visibleColumns.invoiceDate && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('invoice_date')}>Invoice Date{getSortIndicator('invoice_date')}</th>}
                {visibleColumns.dueDate && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('due_date')}>Due Date{getSortIndicator('due_date')}</th>}
                {visibleColumns.total && <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('total')}>Total{getSortIndicator('total')}</th>}
                {visibleColumns.paid && <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('paid_amount')}>Paid{getSortIndicator('paid_amount')}</th>}
                {visibleColumns.balance && <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('balance')}>Balance{getSortIndicator('balance')}</th>}
                {visibleColumns.status && <th className="text-center px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('status')}>Status{getSortIndicator('status')}</th>}
                <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center text-gray-500 text-sm">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center text-red-700 text-sm">
                    Invoice API error: {loadError}
                  </td>
                </tr>
              ) : filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center text-gray-500 text-sm">
                    {searchTerm ? 'No matches.' : 'No invoices yet.'}
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice, index) => (
                  <tr
                    key={invoice.id}
                    className={`hover:bg-indigo-200 transition ${index % 2 === 1 ? 'bg-indigo-100' : 'bg-white'} ${selectedInvoices.includes(invoice.id) ? 'bg-blue-100' : ''}`}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => toggleSelectInvoice(invoice.id)}
                        className="cursor-pointer"
                      />
                    </td>
                    {visibleColumns.invoiceRef && (
                      <td className="px-3 py-2 cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>
                        <div className="font-medium text-gray-900">{invoice.invoice_ref}</div>
                      </td>
                    )}
                    {visibleColumns.customer && (
                      <td className="px-3 py-2 text-gray-700 cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>{invoice.customer_name}</td>
                    )}
                    {visibleColumns.site && (
                      <td className="px-3 py-2 text-gray-700 cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>{invoice.site_name || 'Head Office'}</td>
                    )}
                    {visibleColumns.invoiceDate && (
                      <td className="px-3 py-2 text-gray-700 cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>
                        {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '-'}
                      </td>
                    )}
                    {visibleColumns.dueDate && (
                      <td className="px-3 py-2 text-gray-700 cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>
                        {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                      </td>
                    )}
                    {visibleColumns.total && (
                      <td className="px-3 py-2 text-right font-medium text-gray-900 cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>
                        {formatCurrency(invoice.total)}
                      </td>
                    )}
                    {visibleColumns.paid && (
                      <td className="px-3 py-2 text-right text-green-600 font-medium cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>
                        {formatCurrency(invoice.paid_amount)}
                      </td>
                    )}
                    {visibleColumns.balance && (
                      <td className="px-3 py-2 text-right cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>
                        <span className={`font-medium ${parseFloat(invoice.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(invoice.balance)}
                        </span>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 py-2 text-center cursor-pointer" onClick={() => navigateWithPreference(navigate, `/finance/invoices/${invoice.id}`, 'invoices')}>
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded ${getStatusColor(invoice.status)}`}>
                          {getStatusLabel(invoice.status)}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/finance/invoices/${invoice.id}`);
                        }}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium mr-3"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredInvoices.length} of {invoices.length}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowExportModal(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Export Invoices to CSV</h2>
              
              <div className="space-y-4">
                {/* Selection info */}
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <strong>{selectedInvoices.length > 0 ? selectedInvoices.length : filteredInvoices.length}</strong> invoice(s) will be exported
                  {selectedInvoices.length === 0 && <div className="text-xs text-gray-600 mt-1">No selection = export all visible invoices</div>}
                </div>

                {/* Status filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
                  <select
                    value={exportStatusFilter}
                    onChange={(e) => setExportStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="all">All Statuses</option>
                    <option value="sent">Sent Only</option>
                    <option value="draft">Draft Only</option>
                    <option value="paid">Paid Only</option>
                    <option value="partial">Partially Paid</option>
                    <option value="overdue">Overdue Only</option>
                  </select>
                </div>

                {/* Date range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="date"
                      value={exportDateFrom}
                      onChange={(e) => setExportDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={exportDateTo}
                      onChange={(e) => setExportDateTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>

                {/* Email option */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email To <span className="text-gray-400">(optional - for factoring company)</span>
                  </label>
                  <input
                    type="email"
                    value={exportEmail}
                    onChange={(e) => setExportEmail(e.target.value)}
                    placeholder="invoices@ecap.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleDownloadCSV}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 transition"
                  >
                    📥 Download CSV
                  </button>
                  {exportEmail && (
                    <button
                      onClick={handleEmailCSV}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 transition"
                    >
                      ✉️ Email CSV
                    </button>
                  )}
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded font-medium hover:bg-gray-400 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Invoices;
